import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LockIcon from '@mui/icons-material/Lock';

const STORAGE_PREFIX = 'alamo.columnView.';

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

function uniqueFields(fields = []) {
  return [...new Set(fields.filter(Boolean))];
}

function buildVisibilityModel(columns, visibleFields) {
  const visible = new Set(uniqueFields(visibleFields));
  return columns.reduce((model, column) => {
    if (!column.field) return model;
    if (column.hideable === false) return model;
    model[column.field] = visible.has(column.field);
    return model;
  }, {});
}

function allVisibleModel(columns) {
  return columns.reduce((model, column) => {
    if (column.field && column.hideable !== false) model[column.field] = true;
    return model;
  }, {});
}

function loadSavedModel(storageKey, columns, defaultVisibleFields) {
  const fallback = defaultVisibleFields?.length ? buildVisibilityModel(columns, defaultVisibleFields) : {};
  if (typeof window === 'undefined') return fallback;

  const saved = safeParse(window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`));
  if (!saved?.model || typeof saved.model !== 'object') return fallback;

  const validFields = new Set(columns.map((column) => column.field).filter(Boolean));
  return Object.entries(saved.model).reduce((model, [field, value]) => {
    if (validFields.has(field)) model[field] = Boolean(value);
    return model;
  }, {});
}

export function useSavedColumnView(storageKey, columns, defaultVisibleFields = []) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => loadSavedModel(storageKey, columns, defaultVisibleFields));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${storageKey}`,
      JSON.stringify({ model: columnVisibilityModel, savedAt: new Date().toISOString() })
    );
  }, [storageKey, columnVisibilityModel]);

  const showSimpleView = () => setColumnVisibilityModel(buildVisibilityModel(columns, defaultVisibleFields));
  const showFullView = () => setColumnVisibilityModel(allVisibleModel(columns));
  const resetView = () => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
    setColumnVisibilityModel(defaultVisibleFields?.length ? buildVisibilityModel(columns, defaultVisibleFields) : {});
  };
  const setFieldVisible = (field, visible) => {
    setColumnVisibilityModel((current) => ({ ...current, [field]: visible }));
  };

  return {
    columnVisibilityModel,
    setColumnVisibilityModel,
    setFieldVisible,
    showSimpleView,
    showFullView,
    resetView
  };
}

export default function SavedColumnViewControls({
  columns,
  columnVisibilityModel,
  setFieldVisible,
  showSimpleView,
  showFullView,
  resetView,
  label = 'Columns'
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const configurableColumns = useMemo(
    () => columns.filter((column) => column.field && column.hideable !== false),
    [columns]
  );

  const visibleCount = configurableColumns.filter((column) => columnVisibilityModel[column.field] !== false).length;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button variant="outlined" size="small" startIcon={<ViewColumnIcon />} onClick={(event) => setAnchorEl(event.currentTarget)}>
            {label} ({visibleCount}/{configurableColumns.length})
          </Button>
          <Tooltip title="This saved column view automatically stays locked in for this browser and page until changed or reset.">
            <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
              <LockIcon fontSize="small" />
              <Typography variant="caption">Saved view locked to this page</Typography>
            </Stack>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
          <Button size="small" onClick={showSimpleView}>Simple View</Button>
          <Button size="small" onClick={showFullView}>Full View</Button>
          <Button size="small" color="inherit" onClick={resetView}>Reset</Button>
        </Stack>
      </Stack>

      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { width: 320, maxHeight: 440 } }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">Choose columns for this page</Typography>
          <Typography variant="caption" color="text.secondary">
            Changes save automatically on this computer/browser.
          </Typography>
        </Box>
        <Divider />
        {configurableColumns.map((column) => {
          const checked = columnVisibilityModel[column.field] !== false;
          return (
            <MenuItem key={column.field} dense onClick={() => setFieldVisible(column.field, !checked)}>
              <FormControlLabel
                control={<Checkbox checked={checked} onChange={(event) => setFieldVisible(column.field, event.target.checked)} />}
                label={column.headerName || column.field}
                onClick={(event) => event.stopPropagation()}
                sx={{ width: '100%' }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
